import { useEffect } from 'react';

type LegalDocument = 'privacy' | 'terms';

const EFFECTIVE_DATE = 'September 27, 2026';

function LegalShell({ kind, children }: { kind: LegalDocument; children: React.ReactNode }) {
  const title = kind === 'privacy' ? 'Privacy Notice' : 'Terms of Use';

  useEffect(() => {
    const description = kind === 'privacy'
      ? 'How Persivo handles information across its website, product demos, and Studio.'
      : 'The terms that govern public and evaluation use of Persivo.';
    const canonical = `https://persivo.co/${kind}`;
    const setMeta = (selector: string, value: string) => {
      document.head.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', value);
    };
    document.title = `${title} — Persivo`;
    document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', canonical);
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', `${title} — Persivo`);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', canonical);
    setMeta('meta[name="twitter:title"]', `${title} — Persivo`);
    setMeta('meta[name="twitter:description"]', description);
    window.scrollTo(0, 0);
  }, [kind, title]);

  return <>
    <a className="skip-link" href="#legal-content">Skip to content</a>
    <header className="legal-topbar">
      <div className="wrap legal-topbar-inner">
        <a href="/" className="legal-brand" aria-label="Persivo home">
          <img src="/persivo-lockup-horizontal.svg" alt="Persivo" />
        </a>
        <a href="/" className="legal-back">Back to Persivo <span aria-hidden="true">↗</span></a>
      </div>
    </header>
    <main className="legal-page wrap" id="legal-content">
      <aside className="legal-index" aria-label="Legal documents">
        <p className="eyebrow">LEGAL</p>
        <nav>
          <a href="/privacy" aria-current={kind === 'privacy' ? 'page' : undefined}>Privacy</a>
          <a href="/terms" aria-current={kind === 'terms' ? 'page' : undefined}>Terms</a>
        </nav>
      </aside>
      <article className="legal-document">
        <header className="legal-heading">
          <p className="eyebrow">PERSIVO / {kind === 'privacy' ? 'PRIVACY' : 'TERMS'}</p>
          <h1>{title}</h1>
          <p>Effective {EFFECTIVE_DATE}</p>
        </header>
        {children}
      </article>
    </main>
    <footer className="legal-footer wrap">
      <p>© {new Date().getFullYear()} Persivo</p>
      <nav aria-label="Legal footer">
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
        <a href="mailto:hemant@persivo.co">Contact</a>
      </nav>
    </footer>
  </>;
}

function PrivacyNotice() {
  return <LegalShell kind="privacy">
    <p className="legal-lead">This notice explains how Persivo handles information when you visit our website, use a Persivo product demo, contact us, or use Persivo Studio.</p>

    <section>
      <h2>Information we handle</h2>
      <h3>Website and device information</h3>
      <p>When you visit Persivo, our hosting and security providers may process information such as your IP address, browser and device type, requested pages, timestamps, and diagnostic logs. They may use essential cookies or similar technology to route traffic, maintain a session, prevent abuse, and keep the service working.</p>

      <h3>Demo conversations and activity</h3>
      <p>When you start a demo, we create a session record. It can contain the questions you type or say, the agent’s responses, choices and clicks, screens or product moments shown, whether a step completed, and where the session ended. The company whose product demo you are viewing may receive the conversation and related insights.</p>
      <p>Email addresses and phone numbers found in demo conversation text are replaced with redaction markers before the session record is stored. Other information you choose to provide—including names or details about your work—may remain in the transcript, so please do not share information that is confidential or unnecessary.</p>

      <h3>Microphone and voice</h3>
      <p>Voice is optional. Persivo can access your microphone only after you choose a voice feature and your browser grants permission. Audio is streamed to our speech-processing provider so the demo can understand you. Persivo’s demo session record stores the resulting transcript, not a raw microphone recording. You can deny or revoke microphone permission and use the demo without voice.</p>

      <h3>Messages and pilot requests</h3>
      <p>When you submit the pilot form, Persivo receives your name, work email, product, and the buyer outcome you describe. We use those details to assess and respond to your request. If you contact us by email instead, we receive the address, message, and other information you include.</p>

      <h3>Studio accounts and customer content</h3>
      <p>If you use Persivo Studio or work with us on a pilot, we may handle account identifiers, access roles, authentication records, product recordings, approved claims, flow configurations, and support communications. A recorded product copy may contain text and visual assets supplied by the customer that created it.</p>
    </section>

    <section>
      <h2>How we use information</h2>
      <ul>
        <li>Provide, operate, personalize, and secure Persivo and its demos.</li>
        <li>Transcribe voice, answer questions, and guide visitors through recorded product screens.</li>
        <li>Give the demo owner conversation analytics, identify unanswered questions, and improve its approved story.</li>
        <li>Respond to enquiries, arrange pilots, and support customers.</li>
        <li>Diagnose failures, prevent abuse, enforce usage limits, and comply with law.</li>
      </ul>
      <p>Persivo does not sell personal information or use demo conversations for third-party behavioural advertising.</p>
    </section>

    <section>
      <h2>Our reasons for processing</h2>
      <p>Depending on where you live, we process information as needed to provide a service or take steps you request; for legitimate interests such as operating, securing, and improving Persivo; with consent, including when you grant microphone access; and when necessary to meet legal obligations. You may withdraw consent at any time, although this does not affect earlier processing.</p>
    </section>

    <section>
      <h2>Who receives information</h2>
      <p>We disclose information only as needed to:</p>
      <ul>
        <li>the customer or demo owner responsible for the product experience you use;</li>
        <li>cloud hosting, database, security, email, AI-model, and speech-processing providers acting for us;</li>
        <li>professional advisers, authorities, or other parties when required by law or necessary to protect rights and safety; and</li>
        <li>a successor in connection with a merger, financing, reorganisation, or sale of the business.</li>
      </ul>
      <p>Providers are permitted to handle information only for the services they supply and subject to their contractual and legal obligations.</p>
    </section>

    <section>
      <h2>Retention</h2>
      <p>Persivo demo session records are available to the demo owner for up to 90 days. We retain account information, customer content, support records, and business correspondence only while needed to provide the service, meet contractual or legal requirements, resolve disputes, or maintain appropriate business records. Infrastructure logs and temporary provider data may follow shorter operational schedules. We delete or de-identify information when it is no longer needed, subject to backups and legal holds.</p>
    </section>

    <section>
      <h2>International processing</h2>
      <p>Persivo and its providers may process information in countries other than your own. Where law requires it, we use recognised safeguards for international transfers.</p>
    </section>

    <section>
      <h2>Your choices and rights</h2>
      <p>You can use browser controls to block or remove cookies and revoke microphone permission. Some essential service functions may not work without their required storage.</p>
      <p>Depending on your location, you may have rights to request access, correction, deletion, restriction, portability, or an objection to certain processing. You may also withdraw consent and complain to your local data-protection authority. To make a request, email <a href="mailto:hemant@persivo.co">hemant@persivo.co</a>. We may need to verify your identity and may ask the relevant demo owner to help fulfil a request involving its demo.</p>
    </section>

    <section>
      <h2>Security</h2>
      <p>We use administrative, technical, and organisational safeguards designed to protect information. No internet service is completely secure, so please use care when deciding what to include in a demo conversation.</p>
    </section>

    <section>
      <h2>Children</h2>
      <p>Persivo is a business service and is not directed to children under 18. We do not knowingly collect personal information from children.</p>
    </section>

    <section>
      <h2>Changes and contact</h2>
      <p>We may update this notice as Persivo changes. We will post the revised notice here and change the effective date when an update is material.</p>
      <p>Questions or privacy requests can be sent to <a href="mailto:hemant@persivo.co">hemant@persivo.co</a>.</p>
    </section>
  </LegalShell>;
}

function TermsOfUse() {
  return <LegalShell kind="terms">
    <p className="legal-lead">These Terms of Use govern your access to persivo.co, public Persivo demos, and related services made available without a separate written agreement.</p>

    <section>
      <h2>1. Accepting these terms</h2>
      <p>By accessing or using Persivo, you agree to these terms and our <a href="/privacy">Privacy Notice</a>. If you use Persivo for an organisation, you confirm that you have authority to accept these terms for that organisation. If you do not agree, do not use the service.</p>
      <p>If you or your organisation has a separate signed agreement with Persivo, that agreement controls if it conflicts with these terms.</p>
    </section>

    <section>
      <h2>2. What Persivo provides</h2>
      <p>Persivo creates guided product demonstrations from recorded product screens, approved material, and conversational software. Public demos may use illustrative products, example data, authored sequences, and AI-generated responses. They are demonstrations—not a live production account, a binding product specification, or professional advice.</p>
    </section>

    <section>
      <h2>3. Accounts and access</h2>
      <p>You must provide accurate account information, keep credentials secure, and promptly tell us about suspected unauthorised access. You are responsible for activity under your account and for ensuring that people you authorise comply with these terms.</p>
    </section>

    <section>
      <h2>4. Acceptable use</h2>
      <p>You may not:</p>
      <ul>
        <li>use Persivo unlawfully, deceptively, or to violate another person’s rights;</li>
        <li>probe, disrupt, overload, bypass, or interfere with the service or its access controls;</li>
        <li>introduce malicious code, automate abusive traffic, or attempt unauthorised access;</li>
        <li>reverse engineer the service except where applicable law expressly permits it;</li>
        <li>upload or record content you do not have the right to use; or</li>
        <li>use demo output as the sole basis for legal, financial, employment, medical, or other high-impact decisions.</li>
      </ul>
    </section>

    <section>
      <h2>5. Your content and product recordings</h2>
      <p>You retain ownership of content you provide. You grant Persivo a worldwide, non-exclusive licence to host, reproduce, process, display, and adapt that content only as needed to provide, secure, and support the service.</p>
      <p>You confirm that you have the rights and permissions needed for anything you provide, including product interfaces, trademarks, data, and approved claims. Do not include production secrets, unnecessary personal information, or regulated data unless a separate agreement expressly covers it.</p>
    </section>

    <section>
      <h2>6. Demo conversations and voice</h2>
      <p>Persivo may record demo transcripts and interaction events so the demo owner can understand what visitors asked and how the experience performed. Contact details found in conversation text are redacted as described in the Privacy Notice, but you should still avoid sharing confidential information.</p>
      <p>Voice is optional and requires browser permission. If enabled, microphone audio is processed to produce a transcript and conversational response. You can deny or revoke permission and use non-voice controls where available.</p>
    </section>

    <section>
      <h2>7. Persivo property</h2>
      <p>Persivo and its licensors own the service, software, designs, documentation, brands, and other materials we provide, excluding customer content. These terms give you a limited, revocable, non-transferable right to use the service for its intended purpose. They do not transfer intellectual-property ownership.</p>
      <p>If you send feedback, you permit us to use it without restriction or compensation.</p>
    </section>

    <section>
      <h2>8. Third-party services</h2>
      <p>Persivo relies on third-party infrastructure, authentication, AI-model, and speech-processing services. Their services may be subject to separate terms, and Persivo is not responsible for third-party products or websites outside our control.</p>
    </section>

    <section>
      <h2>9. Pilots and commercial services</h2>
      <p>Sending a pilot request or email does not create a customer relationship or require either party to proceed. Scope, fees, service levels, data-processing terms, and other commercial commitments must be agreed separately in writing.</p>
    </section>

    <section>
      <h2>10. Changes and availability</h2>
      <p>We may modify, suspend, or discontinue parts of Persivo. We may also limit or suspend access when reasonably necessary to protect the service, comply with law, address risk, or respond to a violation of these terms. We will try to give reasonable notice when practical.</p>
    </section>

    <section>
      <h2>11. Disclaimers</h2>
      <p>To the maximum extent permitted by law, services made available without a separate agreement are provided “as is” and “as available.” Persivo disclaims implied warranties of merchantability, fitness for a particular purpose, non-infringement, and uninterrupted or error-free operation. Nothing in these terms excludes a warranty or right that cannot legally be excluded.</p>
    </section>

    <section>
      <h2>12. Limitation of liability</h2>
      <p>To the maximum extent permitted by law, Persivo will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, revenue, data, goodwill, or business opportunity arising from public or evaluation use of the service. Persivo’s aggregate liability arising from that use will not exceed the greater of the amount you paid Persivo for that use during the preceding 12 months or US$100.</p>
      <p>These limits do not apply where liability cannot be limited by law.</p>
    </section>

    <section>
      <h2>13. Ending use</h2>
      <p>You may stop using Persivo at any time. Provisions that by their nature should continue—including ownership, disclaimers, liability limits, and terms concerning content already processed—survive termination.</p>
    </section>

    <section>
      <h2>14. Changes to these terms</h2>
      <p>We may update these terms. We will post revised terms here and update the effective date. Continued use after revised terms take effect means you accept them. If a change materially affects an active paid service, the applicable customer agreement will govern notice.</p>
    </section>

    <section>
      <h2>15. Contact</h2>
      <p>Questions about these terms can be sent to <a href="mailto:hemant@persivo.co">hemant@persivo.co</a>.</p>
    </section>
  </LegalShell>;
}

export function LegalPage({ document }: { document: LegalDocument }) {
  return document === 'privacy' ? <PrivacyNotice /> : <TermsOfUse />;
}
